import { ArrowPathIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import type { ChangeEvent, ReactNode } from 'react';
import { Button } from './Button';
import { IconButton } from './IconButton';
import { Input } from './Input';

export interface FilterBarKeywordFilter {
  value: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onClear?: () => void;
  placeholder: string;
  'aria-label'?: string;
}

export interface FilterBarProps {
  /** Shows a spinner next to the clear button while results are loading. */
  loading?: boolean;
  /** Quick filter controls (e.g. `Select`, `Switch`) — keep to 2-3 per the filtering pattern. */
  children?: ReactNode;
  /** Shows a "Clear" button. Only pass `true` once a filter is actually applied. */
  clearable?: boolean;
  onClear?: () => void;
  /** @default 'Clear' */
  clearButtonLabel?: string;
  /** Keyword/search filter, rendered right-aligned with a search icon and an optional clear button. */
  keyword?: FilterBarKeywordFilter;
}

function ClearKeywordButton({ onClick }: { onClick: () => void }): JSX.Element {
  return (
    <IconButton
      onClick={onClick}
      aria-label="Clear search"
      icon={<XMarkIcon className="h-3.5 w-3.5" />}
      size="sm"
      shape="circle"
      className="hover:bg-muted"
    />
  );
}

// Quick filters + keyword search, one row. Compose above a Table/list; pair
// `clearable`/`onClear` with whatever owns the filter state (usually a
// useState bag or URL search params in the calling page).
export function FilterBar({
  loading = false,
  children,
  clearable = false,
  onClear,
  clearButtonLabel = 'Clear',
  keyword,
}: FilterBarProps): JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-3">
      {children && <div className="flex flex-wrap items-center gap-2">{children}</div>}
      {(clearable || loading) && (
        <div className="flex items-center gap-2">
          {clearable && (
            <Button variant="ghost" size="sm" onClick={onClear}>
              {clearButtonLabel}
            </Button>
          )}
          {loading && (
            <span role="status">
              <ArrowPathIcon className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden="true" />
              <span className="sr-only">Loading…</span>
            </span>
          )}
        </div>
      )}
      {keyword && (
        <div className="w-full sm:ml-auto sm:w-48">
          <Input
            size="sm"
            value={keyword.value}
            onChange={keyword.onChange}
            placeholder={keyword.placeholder}
            aria-label={keyword['aria-label'] ?? keyword.placeholder}
            adornmentStart={<MagnifyingGlassIcon className="h-4 w-4" />}
            adornmentEnd={
              keyword.onClear && keyword.value ? <ClearKeywordButton onClick={keyword.onClear} /> : undefined
            }
          />
        </div>
      )}
    </div>
  );
}
